(() => {
  const questionSets = {
    open: [
      { kicker: 'TURN A LABEL INTO A STORY', text: 'What’s the most last-minute decision you’ve made that turned out better than expected?', why: 'Instead of asking someone to call themselves spontaneous, invite them into a real memory.' },
      { kicker: 'FIND EVERYDAY JOY', text: 'What’s an ordinary part of your week that you genuinely look forward to?', why: 'The smallest rituals can reveal more than a polished list of hobbies.' },
      { kicker: 'LET THEM SHOW YOU', text: 'What’s something you could teach somebody with absolutely no preparation?', why: 'A good answer can open a story about confidence, care, skill, or a delightfully specific obsession.' },
      { kicker: 'NOTICE WHAT STAYED', text: 'What’s a compliment you received years ago that somehow stuck with you?', why: 'What someone remembers being seen for can tell you what mattered beneath the praise.' }
    ],
    play: [
      { kicker: 'MAKE ROOM FOR SURPRISE', text: 'What’s something that irrationally freaks you out?', why: 'A playful answer can lead naturally into childhood memories, funny fears, and unexpected stories.' },
      { kicker: 'RESET THE ENERGY', text: 'What food could you eat an unreasonable amount of?', why: 'Not every good question needs to be deep. Sometimes the conversation needs somewhere easy and lively to go.' },
      { kicker: 'INVITE A LITTLE MISCHIEF', text: 'What’s something you’re embarrassingly easy to convince to do?', why: 'The fun is in the follow-up: who convinces them, and what usually happens next?' },
      { kicker: 'FIND THE QUIRK', text: 'What’s something you’re strangely particular about?', why: 'Specificity creates texture—and often the kind of harmless disagreement that gives a conversation energy.' }
    ],
    clarity: [
      { kicker: 'NAME THE DIRECTION', text: 'Do you see this developing into a committed relationship?', why: 'Clarity is not pressure. A direct question lets both people say what they actually want.' },
      { kicker: 'ASK ABOUT THE EXPERIENCE', text: 'How are you feeling about what we’ve been building?', why: 'This reaches beyond a yes-or-no verdict and invites the other person to describe the relationship as they experience it.' },
      { kicker: 'UNDERSTAND THE HESITATION', text: 'What feels unresolved for you about becoming more serious?', why: 'Instead of arguing with uncertainty, learn what it is actually made of.' },
      { kicker: 'BRING YOURSELF BACK IN', text: 'Would I still choose this if I knew with certainty that it never became more?', why: 'The most important answers are not always theirs. This one helps you notice whether the present truly works for you.' }
    ]
  };

  function init(root) {
    if (root.dataset.stntReady === 'true') return;
    root.dataset.stntReady = 'true';

    let activeTopic = 'open';
    let activeIndex = 0;
    let saved = [];
    try { saved = JSON.parse(localStorage.getItem('stnt-saved') || '[]'); } catch (error) { saved = []; }
    const savedQuestions = new Set(Array.isArray(saved) ? saved : []);
    const kicker = root.querySelector('[data-question-kicker]');
    const text = root.querySelector('[data-question-text]');
    const why = root.querySelector('[data-question-why]');
    const count = root.querySelector('.stnt-question-count');
    const saveButton = root.querySelector('[data-save-question]');
    const savedStatus = root.querySelector('[data-saved-status]');

    const currentQuestion = () => questionSets[activeTopic][activeIndex];
    const renderQuestion = () => {
      const question = currentQuestion();
      kicker.textContent = question.kicker;
      text.textContent = `“${question.text}”`;
      why.textContent = question.why;
      count.textContent = `${activeIndex + 1} of ${questionSets[activeTopic].length}`;
      const isSaved = savedQuestions.has(question.text);
      saveButton.classList.toggle('is-saved', isSaved);
      saveButton.setAttribute('aria-pressed', String(isSaved));
      saveButton.innerHTML = `<span aria-hidden="true">${isSaved ? '♥' : '♡'}</span> ${isSaved ? 'Question saved' : 'Save this question'}`;
    };

    root.querySelectorAll('[data-topic]').forEach((tab) => {
      tab.addEventListener('click', () => {
        root.querySelectorAll('[data-topic]').forEach((item) => {
          const selected = item === tab;
          item.classList.toggle('is-active', selected);
          item.setAttribute('aria-selected', String(selected));
        });
        activeTopic = tab.dataset.topic;
        activeIndex = 0;
        savedStatus.textContent = '';
        renderQuestion();
      });
    });

    root.querySelector('[data-prev-question]')?.addEventListener('click', () => {
      activeIndex = (activeIndex - 1 + questionSets[activeTopic].length) % questionSets[activeTopic].length;
      savedStatus.textContent = '';
      renderQuestion();
    });
    root.querySelector('[data-next-question]')?.addEventListener('click', () => {
      activeIndex = (activeIndex + 1) % questionSets[activeTopic].length;
      savedStatus.textContent = '';
      renderQuestion();
    });
    saveButton?.addEventListener('click', () => {
      const questionText = currentQuestion().text;
      if (savedQuestions.has(questionText)) {
        savedQuestions.delete(questionText);
        savedStatus.textContent = 'Removed from your saved questions.';
      } else {
        savedQuestions.add(questionText);
        savedStatus.textContent = `${savedQuestions.size} question${savedQuestions.size === 1 ? '' : 's'} saved on this device.`;
      }
      try { localStorage.setItem('stnt-saved', JSON.stringify([...savedQuestions])); } catch (error) { /* Storage can be unavailable in strict privacy modes. */ }
      renderQuestion();
    });

    const reframeButton = root.querySelector('[data-reveal-reframe]');
    const reframePanel = root.querySelector('[data-reframe-panel]');
    reframeButton?.addEventListener('click', () => {
      const expanded = reframeButton.getAttribute('aria-expanded') === 'true';
      reframeButton.setAttribute('aria-expanded', String(!expanded));
      reframeButton.innerHTML = `${expanded ? 'Show' : 'Hide'} the stronger question <span aria-hidden="true">${expanded ? '↓' : '↑'}</span>`;
      reframePanel.hidden = expanded;
    });

    const bookObject = root.querySelector('[data-book-object]');
    root.querySelectorAll('[data-cover-side]').forEach((button) => {
      button.addEventListener('click', () => {
        const showBack = button.dataset.coverSide === 'back';
        bookObject?.classList.toggle('is-back', showBack);
        root.querySelectorAll('[data-cover-side]').forEach((item) => {
          const selected = item === button;
          item.classList.toggle('is-active', selected);
          item.setAttribute('aria-pressed', String(selected));
        });
      });
    });

    const dialog = root.querySelector('[data-preview-dialog]');
    const introPreview = root.querySelector('[data-intro-preview]');
    const chapterPreview = root.querySelector('[data-chapter-preview]');
    const dialogTitle = root.querySelector('[data-dialog-title]');
    root.querySelectorAll('[data-preview]').forEach((button) => {
      button.addEventListener('click', () => {
        const intro = button.dataset.preview === 'intro';
        introPreview.hidden = !intro;
        chapterPreview.hidden = intro;
        dialogTitle.textContent = intro ? 'Stop Wasting Time on Small Talk' : 'Why Your Dating Life Feels Bland';
        dialog?.showModal();
      });
    });
    root.querySelector('[data-close-preview]')?.addEventListener('click', () => dialog?.close());
    root.querySelector('[data-preview-cta]')?.addEventListener('click', () => dialog?.close());
    dialog?.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); });

    const drawer = root.querySelector('[data-cart-drawer]');
    const scrim = root.querySelector('[data-scrim]');
    const setCart = (open) => {
      drawer?.classList.toggle('is-open', open);
      drawer?.setAttribute('aria-hidden', String(!open));
      scrim.hidden = !open;
      document.body.classList.toggle('stnt-lock', open);
      if (open) drawer?.querySelector('[data-close-cart]')?.focus();
    };
    root.querySelector('[data-close-cart]')?.addEventListener('click', () => setCart(false));
    scrim?.addEventListener('click', () => setCart(false));
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape') setCart(false); });

    const productForm = root.querySelector('.stnt-product-form');
    productForm?.addEventListener('submit', async (event) => {
      event.preventDefault();
      const submitButton = productForm.querySelector('[type="submit"]');
      const label = productForm.querySelector('[data-add-label]');
      const message = productForm.querySelector('[data-form-message]');
      const originalLabel = label.textContent;
      submitButton.disabled = true;
      label.textContent = 'Adding…';
      message.textContent = '';
      message.classList.remove('is-error');
      try {
        const response = await fetch(root.dataset.cartAddUrl, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: new FormData(productForm)
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || 'This item could not be added to the cart.');
        message.textContent = 'Added to your Shopify cart.';
        setCart(true);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: result }));
      } catch (error) {
        message.textContent = error.message || 'Something went wrong. Please try again.';
        message.classList.add('is-error');
      } finally {
        submitButton.disabled = false;
        label.textContent = originalLabel;
      }
    });

    const revealItems = root.querySelectorAll('.stnt-reveal');
    if ('IntersectionObserver' in window && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.1 });
      revealItems.forEach((item) => observer.observe(item));
    } else {
      revealItems.forEach((item) => item.classList.add('is-visible'));
    }
    renderQuestion();
  }

  const boot = () => document.querySelectorAll('[data-stnt-root]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
